import React, { useCallback } from 'react';
import useImportFindingFileMutation from '../../hooks/useImportFindingFileMutation';
import { ImportFileOutputData, Tenant } from '#/graphql/types';
import { unknownMappingValue } from '#/common/components/ColumnMapper/ColumnMapper';
import type { ObjectImportConfig } from '../asset/SubmitModal';
import SubmitConfirmModal from '../modals/SubmitConfirmModal';
import { buildAssetLookupImportConfig, buildTenantImportConfig } from '../base/commonImportConfigs';

interface FindingSubmitModalProps {
  visible: boolean;
  uploadedFile: File | null;
  mapping: Record<string, string>;
  uniqueField: string;
  selectedTenant?: Tenant;
  onHiding: () => void;
  onImportStart: () => void;
  onSuccess: (importResult: ImportFileOutputData) => void;
  onFailed: (error: unknown) => void;
}

const FindingSubmitModal: React.FC<FindingSubmitModalProps> = ({
  visible,
  uploadedFile,
  mapping,
  uniqueField,
  selectedTenant,
  onHiding,
  onImportStart,
  onSuccess,
  onFailed,
}) => {
  const { importFindingFile } = useImportFindingFileMutation();

  // Generate the import config based on the mapping
  const generateImportConfig = useCallback((): string => {
    if (!selectedTenant || !mapping || Object.keys(mapping).length === 0) {
      return '';
    }

    const isColumnMapped = (field: string) =>
      !!mapping[field] && mapping[field] !== unknownMappingValue.key;

    const tenantImportConfig = buildTenantImportConfig(selectedTenant);
    const assetImportConfig = buildAssetLookupImportConfig(uniqueField, mapping);

    const findingImportConfig: ObjectImportConfig = {
      model: 'FindingModel',
      thirdPartyId: 'thirdPartyId',
      thirdPartyIdSource: 'thirdPartySource',
      columnMap: {
        thirdPartyId: mapping['third_party_id'],
        thirdPartySource: { defaultValue: 'csv' },
        type: mapping['type'],
        shortDescription: mapping['short_description'],
        // Optional fields
        description: isColumnMapped('description') ? mapping['description'] : undefined,
        priority: isColumnMapped('priority') ? mapping['priority'] : undefined,
        cost: isColumnMapped('cost') ? mapping['cost'] : undefined,
        cause: isColumnMapped('cause') ? mapping['cause'] : undefined,
        symptom: isColumnMapped('symptom') ? mapping['symptom'] : undefined,
        dueDate: isColumnMapped('due_date') ? mapping['due_date'] : undefined,
        // Related entities
        tenantId: {
          relatedTo: 'TenantModel',
        },
        assetId: {
          relatedTo: 'AssetModel',
        },
      },
      updateColumns: [
        'type',
        'shortDescription',
        'description',
        'priority',
        'cost',
        'cause',
        'symptom',
        'dueDate',
        'assetId',
      ],
      uniqueForTenant: true,
    };

    const config: ObjectImportConfig[] = [
      tenantImportConfig,
      assetImportConfig,
      findingImportConfig,
    ];

    return JSON.stringify(config);
  }, [uniqueField, mapping, selectedTenant]);

  return (
    <SubmitConfirmModal
      visible={visible}
      uploadedFile={uploadedFile}
      selectedTenant={selectedTenant}
      entityLabelPlural="Findings"
      entityLabelSingularLower="finding"
      generateImportConfig={generateImportConfig}
      importFile={importFindingFile}
      onHiding={onHiding}
      onImportStart={onImportStart}
      onSuccess={onSuccess}
      onFailed={onFailed}
    />
  );
};

export default FindingSubmitModal;
